import { useEffect, useRef } from "react";

import { navigate, useHashRoute } from "../../lib/hooks";
import { pollStorms } from "../../lib/partnerQueries";
import { useSession } from "../../session";
import { CardFile } from "./CardFile";
import { NewPartner } from "./NewPartner";
import { PartnerDetail } from "./PartnerDetail";
import { PartnerReports } from "./PartnerReports";
import { PartnersList } from "./PartnersList";
import { ReviewQueue } from "./ReviewQueue";
import { SendQueue } from "./SendQueue";
import { StormsAdmin } from "./StormsAdmin";
import { TemplatesTool } from "./TemplatesTool";

/**
 * The partner network's screens, and the strip that switches between them.
 *
 * A section rather than a tab per screen across the top of the app: Send is a
 * screen a rep opens forty times in an afternoon and the review queue is one
 * Jonathon opens twice a month, and neither deserves a slot next to Prospects.
 *
 * Routing is on the hash, like everything else here (`useHashRoute`). The
 * `partner=` prefix is a detail page; the rest are the section's own screens.
 */

export const PARTNER_ROUTES = [
  /*
   * The list has a route of its own rather than being the empty hash.
   *
   * `navigate("")` looked right and was not: the empty hash falls back to
   * whatever `view` and `repTab` the shell was already holding, so a rep who
   * followed a link into a partner from their own Pipeline and then pressed
   * Back landed on the Pipeline again. A named route always means this screen.
   */
  "partners",
  "partners-send",
  "partners-queue",
  "partners-templates",
  "partners-storms",
  "partners-reports",
] as const;

/** True when this hash belongs to the partner section. */
export function isPartnerRoute(hash: string): boolean {
  return (
    hash.startsWith("partner=") ||
    (PARTNER_ROUTES as readonly string[]).includes(hash)
  );
}

export function Partners({
  search,
  network,
}: {
  search: string;
  /**
   * `company_settings.partners_enabled`. Off, the section is the business
   * card file and nothing else: scan a card, see every card and what line of
   * work its owner is in. No texts, no referrals, no storms (ADR 0262).
   */
  network: boolean;
}) {
  const hash = useHashRoute();
  const { isAdmin } = useSession();

  /*
   * Ask the poller to look, once, when somebody opens the section.
   *
   * There is no scheduler in this project by decision — the CompanyCam retry
   * queue works the same way (docs/DEPLOYMENT.md). A poll is idempotent, so
   * this costs one request and is the difference between a storm being noticed
   * on the day and being noticed by whoever thinks to press the button.
   *
   * Deliberately silent. A weather service being down is not something to
   * interrupt somebody's screen about; the storms page has a Check now button
   * that reports properly.
   */
  const polled = useRef(false);
  useEffect(() => {
    // Storms are part of the network, not the card file.
    if (!network || polled.current) return;
    polled.current = true;
    void pollStorms().catch(() => {});
  }, [network]);

  const tabs: { route: string; label: string; admin?: boolean }[] = [
    { route: "partners", label: "Partners" },
    { route: "partners-send", label: "Send" },
    { route: "partners-queue", label: "Review", admin: true },
    { route: "partners-storms", label: "Storms", admin: true },
    { route: "partners-templates", label: "Templates", admin: true },
    { route: "partners-reports", label: "Reports" },
  ];

  const body = (() => {
    if (hash === "partner=new") return <NewPartner network={network} />;
    if (hash.startsWith("partner=")) {
      return (
        <PartnerDetail
          partnerId={hash.slice("partner=".length)}
          network={network}
        />
      );
    }
    // The card file has one list and nothing beside it. A storm push or an
    // old bookmark to a network screen lands on the cards.
    if (!network) return <CardFile search={search} />;
    if (hash === "partners-send") return <SendQueue />;
    if (hash === "partners-queue") return <ReviewQueue />;
    if (hash === "partners-templates") return <TemplatesTool />;
    if (hash === "partners-storms") return <StormsAdmin />;
    if (hash === "partners-reports") return <PartnerReports />;
    return <PartnersList search={search} />;
  })();

  if (!network) return <div className="partners">{body}</div>;

  return (
    <div className="partners">
      <div className="partner-tabs">
        {tabs
          .filter((tab) => !tab.admin || isAdmin)
          .map((tab) => (
            <button
              key={tab.route}
              className={`card-tab${
                hash === tab.route ||
                (tab.route === "partners" && hash.startsWith("partner="))
                  ? " active"
                  : ""
              }`}
              onClick={() => navigate(tab.route)}
            >
              {tab.label}
            </button>
          ))}
      </div>
      {body}
    </div>
  );
}
